/**
 * Defera Credit allocator — pure, no I/O (frontend mirror).
 *
 * Given a checkout's deposit + installment schedule and a customer's available
 * Defera Credit balance, decide how the credit is consumed:
 *   1) the deposit first, then
 *   2) installments front-to-back,
 * until the credit is exhausted.
 *
 * This is a 1:1 mirror of the Edge/Deno source of truth at
 * supabase/functions/_shared/defera-credit-allocator.ts — KEEP THE TWO IN
 * LOCKSTEP. The checkout UI uses this to render the net deposit, the zeroed
 * early installments, and the shifted "next payment" date.
 *
 * All amounts are integer cents. The function never mutates its inputs.
 *
 * @typedef {Object} InstallmentInput
 * @property {number} installment_number 1-indexed installment number
 * @property {number} amount_cents Gross amount of this installment (before credit)
 * @property {string} [due_date] Optional date string, passed through untouched
 *
 * @typedef {InstallmentInput & {
 *   credit_applied_cents: number,
 *   net_cents: number,
 *   fully_covered: boolean,
 * }} AllocatedInstallment
 *
 * @typedef {Object} CreditAllocation
 * @property {number} available_credit_cents
 * @property {number} deposit_gross_cents
 * @property {number} deposit_credit_cents
 * @property {number} net_deposit_cents
 * @property {AllocatedInstallment[]} installments
 * @property {number} installment_credit_cents
 * @property {number} total_credit_consumed_cents
 * @property {number} remaining_credit_cents
 * @property {AllocatedInstallment|null} first_uncovered_installment
 */

function toNonNegInt(value) {
  const n = Math.floor(Number(value) || 0);
  return n > 0 ? n : 0;
}

/**
 * @param {{ depositCents: number, installments: InstallmentInput[], availableCreditCents: number }} params
 * @returns {CreditAllocation}
 */
export function allocateDeferaCredit(params) {
  const depositGross = toNonNegInt(params.depositCents);
  const available = toNonNegInt(params.availableCreditCents);
  let left = available;

  const depositCredit = Math.min(left, depositGross);
  left -= depositCredit;

  const installments = (params.installments || []).map((inst) => {
    const gross = toNonNegInt(inst.amount_cents);
    const applied = Math.min(left, gross);
    left -= applied;
    const net = gross - applied;
    return {
      ...inst,
      amount_cents: gross,
      credit_applied_cents: applied,
      net_cents: net,
      fully_covered: gross > 0 && net === 0,
    };
  });

  const installmentCredit = installments.reduce((sum, i) => sum + i.credit_applied_cents, 0);
  const firstUncovered = installments.find((i) => i.net_cents > 0) ?? null;

  return {
    available_credit_cents: available,
    deposit_gross_cents: depositGross,
    deposit_credit_cents: depositCredit,
    net_deposit_cents: depositGross - depositCredit,
    installments,
    installment_credit_cents: installmentCredit,
    total_credit_consumed_cents: depositCredit + installmentCredit,
    remaining_credit_cents: left,
    first_uncovered_installment: firstUncovered,
  };
}
